import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import type { AdminUser } from "@/hooks/admin/useAdminUsers";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User as UserIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface UsersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser | null;
}

function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={"text-xs text-muted-foreground" + (className ? ` ${className}` : "")}>{children}</div>;
}

function Value({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={"text-sm break-all" + (className ? ` ${className}` : "")}>{children}</div>;
}

export default function UsersDrawer({ open, onOpenChange, user }: UsersDrawerProps) {
  const queryClient = useQueryClient();
  const dt = (s?: string | null) => (s ? new Date(s).toLocaleString() : "-");

  const isWaitlist = user?.source === "waitlist";
  const isProfile = user?.source === "profile";
  const isActive = user?.status === "active";
  const [roleInput, setRoleInput] = useState<string>(user?.role ?? "");

  useEffect(() => {
    setRoleInput(user?.role ?? "");
  }, [user?.id, user?.role]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const handleApprove = async () => {
    if (!user?.id || !isWaitlist) return;
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "approve_waitlist", waitlist_ids: [user.id] } });
    if (error) { toast.error(error.message || "Approval failed"); return; }
    toast.success("Invite sent");
    await refresh();
  };

  const handleInvite = async () => {
    if (!user?.email || !isWaitlist) return;
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "invite", emails: [user.email] } });
    if (error) { toast.error(error.message || "Invite failed"); return; }
    toast.success("Invite sent");
    await refresh();
  };

  const handleActivate = async () => {
    if (!user?.id || !isProfile) return;
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "activate", profile_ids: [user.id] } });
    if (error) { toast.error(error.message || "Activate failed"); return; }
    toast.success("User activated");
    await refresh();
  };

  const handleDeactivate = async () => {
    if (!user?.id || !isProfile) return;
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "deactivate", profile_ids: [user.id] } });
    if (error) { toast.error(error.message || "Deactivate failed"); return; }
    toast.success("User deactivated");
    await refresh();
  };

  const handleDelete = async () => {
    if (!user?.id) return;
    if (!window.confirm("Soft-delete this user? They will be purged after 7 days.")) return;
    const payload = user.source === "profile" ? { profile_ids: [user.id] } : { waitlist_ids: [user.id] };
    const { error } = await supabase.functions.invoke("admin-users-manage", { body: { action: "delete", ...(payload as any) } });
    if (error) { toast.error(error.message || "Delete failed"); return; }
    toast.success("User deleted (soft-delete)");
    await refresh();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px]">
        <SheetHeader>
          <SheetTitle>User</SheetTitle>
          <SheetDescription>&nbsp;</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-12 w-12 rounded-full">
              {user?.avatar_url ? (
                <AvatarImage
                  src={user.avatar_url}
                  alt={user?.name || user?.email || ""}
                  onError={(e: any) => { try { e.currentTarget.style.display = 'none' } catch {} }}
                />
              ) : null}
              <AvatarFallback delayMs={200} className="rounded-full p-0 bg-muted text-muted-foreground">
                <UserIcon className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-base font-medium truncate">{user?.name || "-"}</div>
              <div className="text-sm text-muted-foreground truncate">{user?.email || "-"}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="capitalize">{user?.status || "-"}</Badge>
                <Badge variant="outline" className="capitalize">{user?.source || "-"}</Badge>
                {user?.provider_type && <Badge variant="outline">{user.provider_type}</Badge>}
              </div>
            </div>
          </div>

          <div>
            <Label>Name</Label>
            <Value>{user?.name || "-"}</Value>
          </div>
          <div>
            <Label>Email</Label>
            <Value>{user?.email || "-"}</Value>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Role</Label>
              {isProfile ? (
                <div className="mt-1">
                  <Select value={roleInput} onValueChange={(v) => setRoleInput(v)}>
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="advisor">Advisor</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="sys_admin">System Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <Value>{user?.role || "-"}</Value>
              )}
            </div>
            <div>
              <Label>Status</Label>
              <div className="mt-0.5">
                <Badge variant="secondary" className="capitalize">{user?.status || "-"}</Badge>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Source</Label>
              <Value className="capitalize">{user?.source || "-"}</Value>
            </div>
            <div>
              <Label>Provider</Label>
              <Value>{user?.provider_type || "-"}</Value>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Last auth method</Label>
              <Value>{user?.last_auth_method || "-"}</Value>
            </div>
            <div>
              <Label>Last login</Label>
              <Value>{dt(user?.last_login)}</Value>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Created</Label>
              <Value>{dt(user?.created_at)}</Value>
            </div>
            <div>
              <Label>Updated</Label>
              <Value>{dt(user?.updated_at)}</Value>
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {isWaitlist && (
            <>
              <Button size="sm" variant="default" onClick={handleApprove}>Approve</Button>
              <Button size="sm" variant="outline" onClick={handleInvite}>Invite / Resend</Button>
            </>
          )}
          {isProfile && !isActive && (
            <Button size="sm" variant="default" onClick={handleActivate}>Activate</Button>
          )}
          {isProfile && isActive && (
            <Button size="sm" variant="outline" onClick={handleDeactivate}>Deactivate</Button>
          )}
          {isProfile && (
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                if (!user?.id || !roleInput) return;
                const { error } = await supabase.functions.invoke("admin-users-manage", {
                  body: { action: "set_role", profile_ids: [user.id], role: roleInput },
                });
                if (error) { toast.error(error.message || "Set role failed"); return; }
                toast.success("Role updated");
                await refresh();
              }}
            >
              Save Role
            </Button>
          )}
          {!!user && (
            <Button size="sm" variant="destructive" onClick={handleDelete}>Delete</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
